/**
 * SW2-2459 — the one way an offline project is synced, by the dashboard button or automatically.
 *
 *   - One sync at a time. Two runs over the same queue send it twice (duplicate findings and photos,
 *     a second signature refused). A request for a project already syncing joins that run (and
 *     gets its progress); any other waits its turn.
 *   - A run keeps going while it makes progress, so work saved while it was sending (an offline
 *     note, a signature) goes in the same run instead of waiting for the next trigger.
 *   - The automatic sync only uploads. Removing a download stays a decision the user makes.
 *   - It syncs only projects the logged-in user downloaded, and steps aside for the user's own
 *     requests (offlineSyncSend `setBeforeSend`) — until the user asks for that sync themselves.
 *   - A connection lost mid-sync is retried with back-off; work the server refused is left for the
 *     user to see, not retried in a loop. Nothing is attempted while the device reports offline;
 *     the `online` trigger takes over.
 *
 * A web page cannot run with the screen off or in the background (and iOS stops it outright), so
 * the triggers are: the app starting, the device coming back online, and the user returning to it.
 */
import db, { countUnsyncedWork } from '../db/db';
import { waitForForegroundIdle } from './foregroundRequests';
import { syncOfflineProject } from './offlineProjectSync';
import { ConnectionLost, setBeforeSend } from './offlineSyncSend';

const MAX_PASSES = 5;
const RETRY_DELAYS_MS = [30000, 60000, 120000, 300000];

/** projectId → { promise, callbacks: Set<{ onStatus, onProgress }>, background } */
const inFlight = new Map();
const listeners = new Set();
/** Status of each project's last or current run (see `emit`). */
const status = new Map();

let chain = Promise.resolve();

let autoRunning = false;

let retryTimer = null;

let retryIndex = 0;

const emit = (next) => {
  status.set(next.projectId, next);
  listeners.forEach((fn) => fn(next));
};

export const subscribeSyncStatus = (fn) => {
  listeners.add(fn);

  return () => listeners.delete(fn);
};

export const getSyncStatus = (projectId) => status.get(projectId);

export const isSyncing = (projectId) => inFlight.has(projectId);

/** A sentence for a sync result whose signatures were held back (empty when none were). */
export const heldSignaturesNote = (heldSignatures = []) => {
  const n = heldSignatures.length;

  if (!n) return '';

  return n === 1
    ? ' The inspection signature has not been sent yet, so its completion report has not gone out.'
    : ` ${n} inspection signatures have not been sent yet, so their completion reports have not gone out.`;
};

/**
 * SW2-2459 × SW2-2248 — a sentence for signatures the sync sent that raised a review (the server's
 * `reviewRaised`), so the signer doesn't take "uploaded" to mean the report went out. Empty when none
 * did, and always before the backend returns `reviewRaised` (backend #479): its old body was empty.
 */
export const reviewsRaisedNote = (sentSignatures = []) => {
  const n = new Set(sentSignatures.filter((s) => s.reviewRaised).map((s) => s.inspectionId)).size;

  if (!n) return '';

  return n === 1
    ? ' The inspection was sent for review; its report will be emailed once it is approved.'
    : ` ${n} inspections were sent for review; their reports will be emailed once they are approved.`;
};

/** What to tell the user about signatures the server refused (empty when none were). */
export const refusedSignaturesNote = (refusedSignatures = []) =>
  refusedSignatures
    .map(
      (r) =>
        `The signature on inspection ${r.inspectionId} was not accepted: ${r.reason}. It has been removed from this device — open the inspection and sign again.`,
    )
    .join(' ');

/**
 * Wait for the user's own requests to finish before each automatic write. `quietMs: 0`: the sync's
 * own requests count as foreground too, and a quiet period would make it wait on itself before
 * every write.
 */
const stepAside = () => waitForForegroundIdle({ quietMs: 0 });

/**
 * Before each write of a run: is the download's owner still the one signed in? Signing out doesn't
 * stop a run, so after another user signed in the rest of the previous user's work went up under
 * theirs. The run stops instead, its work kept for its owner (PR #156 review). An automatic run
 * the user hasn't asked for first waits for their own requests.
 */
const beforeEachWrite = (entry, ownerId) => async () => {
  // Waited first, checked after: the wait can be long, and the user can change during it (PR #156).
  if (entry.background && !entry.userAsked) await stepAside();

  const current = (await db.user.get(1))?.user_id;

  // No stored user: who is signed in can't be told. An error, so the user is told the upload
  // stopped — taken as a change of user, it stopped without a word, and nothing ever uploaded.
  if (current == null) throw new Error('No user is stored on this device, so the upload was stopped');

  if (current !== ownerId) {
    entry.sessionChanged = true;
    throw new ConnectionLost(`project ${entry.projectId}: the signed-in user changed`);
  }
};

/** Keep the screen on while a sync runs (iOS 16.4+, Chrome); a sleeping phone stops the page. */
async function holdScreenOn() {
  try {
    if (navigator.wakeLock && document.visibilityState === 'visible') {
      const lock = await navigator.wakeLock.request('screen');

      return () => lock.release().catch(() => {});
    }
  } catch (err) {
    // Not granted (battery saver, unsupported): the sync still runs.
  }

  return () => {};
}

/**
 * PR #150 review — one sync across tabs too. The queue is in IndexedDB, which every tab of the app
 * shares, so two tabs syncing at once replay the same rows. Web Locks (Chrome, Safari 15.4+): an
 * automatic run that finds another tab syncing skips this time; a sync the user asked for waits.
 * Without Web Locks, the lock is this tab's alone, as before.
 */
async function withTabLock(background, fn) {
  if (!navigator.locks || !navigator.locks.request) return fn();

  const SKIPPED = Symbol('skipped');
  const result = await navigator.locks.request(
    'sw2-offline-sync',
    background ? { ifAvailable: true } : {},
    (lock) => (lock ? fn() : SKIPPED),
  );

  return result === SKIPPED ? { remaining: null, interrupted: false, skipped: true } : result;
}

async function run(projectId, entry) {
  return withTabLock(entry.background, () => runLocked(projectId, entry));
}

async function runLocked(projectId, entry) {
  const each = (key) => (...args) => entry.callbacks.forEach((cb) => cb[key](...args));
  const onStatus = each('onStatus');
  const onProgress = each('onProgress');

  let release = () => {};

  emit({ projectId, syncing: true, background: entry.background });

  try {
    const project = await db.projects.get(projectId);

    if (!project) {
      // A manual sync of a project that is not on the device is an error, as it always was;
      // reporting "synced" would let the caller remove what is left of it.
      if (!entry.background) throw new Error('Project not found');

      const missing = { remaining: 0, interrupted: false, missing: true };

      emit({ projectId, syncing: false, background: entry.background, ...missing });

      return missing;
    }

    // The download's owner: a run can wait behind another, and start after a change of user.
    setBeforeSend(beforeEachWrite(entry, project.downloadedBy ?? (await db.user.get(1))?.user_id));
    release = await holdScreenOn();

    let result = { remaining: 0, interrupted: false, heldSignatures: [] };
    const sentSignatures = [];
    const refusedSignatures = [];

    for (let pass = 0; pass < MAX_PASSES; pass++) {
      const before = await countUnsyncedWork(projectId);

      if (before === 0 && pass > 0) break;

      entry.dirty = false;
      result = await syncOfflineProject(projectId, { onStatus, onProgress });
      sentSignatures.push(...(result.sentSignatures || []));
      refusedSignatures.push(...(result.refusedSignatures || []));

      // Stop when done, offline, or when a pass changed nothing (only refused work is left) —
      // unless work was added during the pass (a signature queued meanwhile, PR 2 review): the
      // pass may have passed its inspection already, and it is not refused, just not tried yet.
      // That holds for "nothing left" too: the pass counted before the work was added (PR #156).
      if (result.interrupted) break;
      if (!entry.dirty && (result.remaining === 0 || result.remaining >= before)) break;
    }

    // Every pass's, not just the last: a signature often goes in the pass after the work it waited on.
    result = {
      ...result,
      sentSignatures,
      refusedSignatures,
      // Stopped for a change of user, not a lost connection: not this user's to hear about.
      ...(entry.sessionChanged ? { sessionChanged: true } : {}),
    };

    emit({ projectId, syncing: false, background: entry.background, ...result });

    return result;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error(`Offline sync failed for project ${projectId}`, err);
    emit({ projectId, syncing: false, background: entry.background, remaining: null, interrupted: false, error: err });
    throw err;
  } finally {
    setBeforeSend(null);
    release();
  }
}

/**
 * Sync one downloaded project. `background` marks an automatic run. Resolves with
 * `{ remaining, interrupted, heldSignatures }` (see syncOfflineProject); the caller decides what to
 * do with the local copy.
 */
export function runProjectSync(
  projectId,
  { onStatus = () => {}, onProgress = () => {}, background = false } = {},
) {
  const callbacks = { onStatus, onProgress };
  const joining = inFlight.get(projectId);

  if (joining) {
    joining.callbacks.add(callbacks);
    // Something asked again while it runs: new work may have been queued. One more pass.
    joining.dirty = true;

    if (!background && joining.background && !joining.userAsked) {
      // The user asked for this sync: it no longer waits on their requests. `background` stays
      // as it was — it says how the run started, and the automatic toast it opened must see the
      // run finish.
      joining.userAsked = true;
    }

    return joining.promise;
  }

  if (inFlight.size) onStatus('Waiting for another sync to finish...');

  const entry = { callbacks: new Set([callbacks]), background, projectId };

  entry.promise = chain.then(() => run(projectId, entry));
  chain = entry.promise.catch(() => {});
  inFlight.set(projectId, entry);
  entry.promise.then(
    () => inFlight.delete(projectId),
    () => inFlight.delete(projectId),
  );

  return entry.promise;
}

/** Retries of `sendQueuedWork`, cancelled with the automatic sync's (logout). */
const queuedRetries = new Set();
const QUEUED_RETRY_ATTEMPTS = 6; // with RETRY_DELAYS_MS, about 15 minutes

/** Is this download still the logged-in user's? A retry must not send it under someone else. */
const ownedByCurrentUser = async (projectId) => {
  const [project, user] = await Promise.all([db.projects.get(projectId), db.user.get(1)]);

  return Boolean(project && user?.user_id != null && project.downloadedBy === user.user_id);
};

/**
 * PR 2 — send what was just queued (a signature) for a project. If another tab holds the sync lock,
 * or the connection drops part-way, try again with the automatic sync's back-off rather than leave
 * it until the next app start or reconnect (PR #156 review). Only while the download is still the
 * logged-in user's.
 */
export function sendQueuedWork(projectId, attempt = 0) {
  return runProjectSync(projectId, { background: true })
    .then((result) => {
      if (!result?.skipped && !result?.interrupted) return result;

      // Offline, the `online` event sends it.
      if (navigator.onLine === false) return result;

      if (attempt >= QUEUED_RETRY_ATTEMPTS) {
        // eslint-disable-next-line no-console
        console.warn(
          `Offline sync: stopped retrying project ${projectId}; it is sent on the next app start, reconnect or return to the app`,
        );

        return result;
      }

      const timer = setTimeout(async () => {
        queuedRetries.delete(timer);

        if (await ownedByCurrentUser(projectId).catch(() => false)) sendQueuedWork(projectId, attempt + 1);
      }, RETRY_DELAYS_MS[Math.min(attempt, RETRY_DELAYS_MS.length - 1)]);

      queuedRetries.add(timer);

      return result;
    })
    .catch(() => {
      // Logged and reported by `run`.
    });
}

export function cancelAutoSyncRetry() {
  clearTimeout(retryTimer);
  retryTimer = null;
  queuedRetries.forEach((timer) => clearTimeout(timer));
  queuedRetries.clear();
}

function scheduleRetry(userId) {
  if (retryTimer) return;

  const delay = RETRY_DELAYS_MS[Math.min(retryIndex, RETRY_DELAYS_MS.length - 1)];

  retryIndex += 1;
  retryTimer = setTimeout(() => {
    retryTimer = null;
    autoSyncDownloads({ userId });
  }, delay);
}

/**
 * Upload the queued work of every project the user downloaded on this device. Never removes a
 * download, never throws. Downloads made before SW2-2459 carry no `downloadedBy`; they are left to
 * the manual sync rather than pushed under whoever is logged in now, and reported through
 * `onLegacy(projects)` so the user knows to press Sync.
 */
/**
 * @param {{ userId?: number, onLegacy?: (projects: any[]) => void }} options
 */
export async function autoSyncDownloads({ userId, onLegacy = (projects) => {} }) {
  // `onLine` false is reliable (true is not): nothing can be sent, and `online` will trigger again.
  if (!userId || autoRunning || navigator.onLine === false) return;

  autoRunning = true;

  try {
    const projects = await db.projects.toArray();
    const legacy = [];

    let interrupted = false;

    for (const project of projects) {
      try {
        if ((await countUnsyncedWork(project.id)) === 0) continue;

        if (project.downloadedBy !== userId) {
          if (project.downloadedBy == null) legacy.push(project);

          continue;
        }

        const result = await runProjectSync(project.id, { background: true });

        if (result.interrupted) {
          interrupted = true;
          break;
        }
      } catch (err) {
        // Logged and reported by `run`; one project's failure must not stop the others.
      }
    }

    if (legacy.length) onLegacy(legacy);

    if (interrupted) {
      scheduleRetry(userId);
    } else {
      retryIndex = 0;
      cancelAutoSyncRetry();
    }
  } catch (err) {
    // The device database could not be read (closed, upgraded in another tab, out of space).
    // eslint-disable-next-line no-console
    console.error('Automatic offline sync failed', err);
  } finally {
    autoRunning = false;
  }
}

/** Tests only. */
export function resetSyncCoordinator() {
  inFlight.clear();
  status.clear();
  listeners.clear();
  chain = Promise.resolve();
  autoRunning = false;
  retryIndex = 0;
  cancelAutoSyncRetry();
}
